import { COURAGES, LIEUX } from '@nextilt/commun';
import type { ReglagesDeMaintenant } from './reglagesDeMaintenant';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import {
  auMilieu,
  cransDuTemps,
  MOTS_COURAGE,
  MOTS_LIEU,
  N_IMPORTE_OU,
  REGLAGE_COURAGE,
  REGLAGE_LIEU,
  REGLAGE_TEMPS,
  TOUT_LE_TEMPS,
} from '../partage/vocabulaire';

/**
 * Les réglages de « Maintenant », repliés sur une ligne (chantier `revue-design`, ticket 01,
 * `design/maintenant.html`, décisions d'Igor du 2026-10-03, « Maintenant » 4 et 5) :
 * « J'ai la journée ▾ · Je suis dehors ▾ · Courage normal ▾ ». Fermé, chacun dit sa valeur ; un
 * clic déplie ses options, un choix referme. Sur l'ordinateur comme au téléphone. La page se
 * recalcule à chaque choix ; les réglages se gardent sur l'appareil (`reglagesDeMaintenant`).
 *
 * « J'ai » et « Je suis » peuvent ne rien retenir : « Tout le temps », « N'importe où ».
 * « Courage » en retient toujours un, Normal par défaut ; il ne retire rien, il change l'ordre
 * de « Pendant ce temps ».
 */
export function PastillesDeReglage({
  reglages,
  onChanger,
}: {
  reglages: ReglagesDeMaintenant;
  onChanger: (r: Partial<ReglagesDeMaintenant>) => void;
}) {
  const { temps, lieu, courage } = reglages;
  return (
    <div className="reglages-maintenant" data-tuto="bouton-filtrer">
      <Reglage
        nom={REGLAGE_TEMPS}
        tuto="reglage-temps"
        choisi={temps}
        options={[[null, TOUT_LE_TEMPS], ...cransDuTemps()]}
        onChoisir={(t) => onChanger({ temps: t })}
      />
      <Reglage
        nom={REGLAGE_LIEU}
        choisi={lieu}
        options={[[null, N_IMPORTE_OU], ...LIEUX.map((l) => [l, MOTS_LIEU[l]] as const)]}
        onChoisir={(l) => onChanger({ lieu: l })}
      />
      <Reglage
        nom={REGLAGE_COURAGE}
        choisi={courage}
        options={COURAGES.map((c) => [c, MOTS_COURAGE[c]] as const)}
        onChoisir={(c) => c && onChanger({ courage: c })}
      />
    </div>
  );
}

function Reglage<V extends string>({
  nom,
  tuto,
  choisi,
  options,
  onChoisir,
}: {
  nom: string;
  /** Le repère du tuto (« J'ai », que « Trier tes tâches » fait régler, chantier `aide`, ticket 04). */
  tuto?: string;
  choisi: V | null;
  options: readonly (readonly [V | null, string])[];
  onChoisir: (v: V | null) => void;
}) {
  const menu = useMenuOuvert();
  const mot = options.find(([v]) => v === choisi)?.[1] ?? options[0]?.[1] ?? '';
  const choisir = (v: V | null) => {
    onChoisir(v);
    menu.setOuvert(false);
  };

  return (
    <div className="reglage" ref={menu.ref} data-tuto={tuto}>
      <button
        type="button"
        className="reglage-bouton"
        aria-haspopup="menu"
        aria-expanded={menu.ouvert}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        <span className="reglage-nom">{nom}</span>
        <span className="reglage-valeur">{auMilieu(mot)}</span>
        <Chevron />
      </button>
      {menu.ouvert && (
        <div className="reglage-menu" role="menu" aria-label={nom}>
          {options.map(([v, m]) => (
            <button
              key={v ?? 'aucun'}
              type="button"
              role="menuitemradio"
              aria-checked={v === choisi}
              className={v === choisi ? 'est-choisi' : ''}
              onClick={() => choisir(v)}
            >
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Le chevron de la maquette, à l'encre du bouton. */
export function Chevron() {
  return (
    <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M6 9l6 6 6-6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
